import React, { useState } from 'react';
import { IconButton, Menu, MenuItem, Tooltip, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import MoreHorizIcon from '@material-ui/icons/MoreHoriz';
import ReplayIcon from '@material-ui/icons/Replay';
import DeleteOutlineIcon from '@material-ui/icons/DeleteOutline';
import { useTranslation } from 'react-i18next';
import { MyTimePicker } from './DatePicker';
import { ValueBox } from './SettingGroup';
import { GetHelp, InfoButton } from './Description';
import { switchValuesMatch } from './SettingsSwitch';
import {
  formatTimeParts,
  readTimeParts,
  secondsToTimeParts,
  timePartsToSeconds
} from '../Utils/time_utils';

const useStyles = makeStyles((theme) => ({
  header: { display: 'flex', alignItems: 'center', gap: 8, minHeight: 44 },
  title: { flex: 1, minWidth: 0, fontSize: '0.95rem' },
  status: {
    width: 8,
    height: 8,
    flexShrink: 0,
    borderRadius: '50%',
    backgroundColor: theme.palette.primary.main,
    '&[data-enabled="false"]': { backgroundColor: theme.palette.text.disabled },
    '&[data-unsaved="true"]': { boxShadow: `0 0 0 2px ${theme.palette.secondary.main}` }
  },
  clock: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap'
  },
  fields: { display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 12 },
  label: { display: 'flex', alignItems: 'center', minHeight: 32 },
  labelText: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontSize: '0.8rem'
  },
  field: {
    '& input': { fontSize: '1.125rem', padding: '8px 0' },
    '&[data-unsaved="true"] .MuiInput-underline:not(.Mui-error):before': {
      borderBottom: `2px solid ${theme.palette.secondary.main}`
    }
  },
  more: { width: 44, height: 44, marginRight: -8, color: 'grey' },
  menuItem: { minHeight: 44, gap: 12 },
  delete: { color: theme.palette.error.main }
}));

function EventTimeField({
  field,
  value,
  controllerValue,
  seconds,
  min,
  max,
  error,
  disabled,
  controller,
  id,
  controls,
  getTimeRange,
  eventLabel
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const help = GetHelp(id, field, controller.type);
  const changed = value !== controllerValue;
  const scale = seconds ? 1 : 60;
  return (
    <div>
      <div className={classes.label}>
        <Typography className={classes.labelText} title={t(field)}>
          {t(field)}
        </Typography>
        <InfoButton content={help.content} img={help.img} />
        {changed && controllerValue !== undefined && (
          <IconButton
            size="small"
            aria-label={`${t('reset')}: ${t(field)}`}
            disabled={disabled}
            onClick={() => controls.resetSetting(id)}
          >
            <ReplayIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      <MyTimePicker
        className={classes.field}
        data-unsaved={changed ? 'true' : undefined}
        name={field}
        dialogTitle={`${eventLabel} ${t(seconds ? 'duration_seconds' : 'event_start')}`}
        fullWidth
        margin="none"
        value={formatTimeParts(secondsToTimeParts(value * scale), seconds)}
        seconds={seconds}
        minSeconds={min * scale}
        maxSeconds={max * scale}
        error={error}
        disabled={disabled}
        renderTimePreview={(date) => (
          <output aria-label={t('event_clock_time')} title={t('event_clock_time')}>
            {getTimeRange(timePartsToSeconds(readTimeParts(date)) / scale)}
          </output>
        )}
        onChange={(date) => controls.setValue(id, timePartsToSeconds(readTimeParts(date)) / scale)}
      />
    </div>
  );
}

export default function IrrigationEvent({
  event,
  savedEvent,
  index,
  number,
  getClockTime,
  minStart,
  maxStart,
  startError,
  durationError,
  controller,
  controls,
  writeAccess,
  onDelete,
  marker,
  valuePath = 'irrigation_events'
}) {
  const { t } = useTranslation();
  const classes = useStyles();
  const [anchor, setAnchor] = useState(null);
  const eventLabel = `P${event.phase}-${number}`;
  const title = `P${event.phase} ${t('irrigation_event_short')} ${number}`;
  const disabled = !writeAccess || !event.enabled;
  const status = t(event.enabled ? 'enabled' : 'disabled');
  const path = `${valuePath}[${index}]`;
  const getTimeRange = (startMinute, durationSeconds) => {
    const start = getClockTime(startMinute);
    const endSeconds = (timePartsToSeconds(readTimeParts(start)) + durationSeconds) % (24 * 3600);
    const end = formatTimeParts(secondsToTimeParts(endSeconds), endSeconds % 60 !== 0);
    return `${start}–${end}`;
  };
  const timeRange = getTimeRange(event.start_minute_from_phase_begin, event.duration_seconds);
  return (
    <ValueBox
      controller={controller}
      id={`irrigation_event_${number}`}
      style={{ margin: 0, padding: '4px 12px 12px', whiteSpace: 'normal' }}
    >
      <div className={classes.header}>
        {marker}
        <span
          className={classes.status}
          role="img"
          aria-label={status}
          title={status}
          data-enabled={Boolean(event.enabled)}
          data-unsaved={
            savedEvent && !switchValuesMatch(event.enabled, savedEvent.enabled) ? 'true' : undefined
          }
        />
        <Typography className={classes.title}>{title}</Typography>
        <Typography className={classes.clock} title={`${t('event_clock_time')}: ${timeRange}`}>
          {timeRange}
        </Typography>
        <Tooltip title={t('more')}>
          <IconButton
            className={classes.more}
            aria-label={`${t('more')}: ${title}`}
            aria-haspopup="menu"
            aria-expanded={Boolean(anchor)}
            onClick={(e) => setAnchor(e.currentTarget)}
          >
            <MoreHorizIcon />
          </IconButton>
        </Tooltip>
      </div>
      <div className={classes.fields}>
        <EventTimeField
          field="start_minute_from_phase_begin"
          value={event.start_minute_from_phase_begin}
          controllerValue={savedEvent?.start_minute_from_phase_begin}
          seconds={false}
          min={minStart}
          max={maxStart}
          error={startError}
          disabled={disabled}
          controller={controller}
          id={`${path}.start_minute_from_phase_begin`}
          controls={controls}
          eventLabel={eventLabel}
          getTimeRange={(startMinute) => getTimeRange(startMinute, event.duration_seconds)}
        />
        <EventTimeField
          field="duration_seconds"
          value={event.duration_seconds}
          controllerValue={savedEvent?.duration_seconds}
          seconds
          min={0}
          max={3600}
          error={durationError}
          disabled={disabled}
          controller={controller}
          id={`${path}.duration_seconds`}
          controls={controls}
          eventLabel={eventLabel}
          getTimeRange={(durationSeconds) =>
            getTimeRange(event.start_minute_from_phase_begin, durationSeconds)
          }
        />
      </div>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        getContentAnchorEl={null}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          className={classes.menuItem}
          disabled={!writeAccess}
          onClick={() => {
            if (!writeAccess) return;
            controls.setValue(`${path}.enabled`, !event.enabled);
            setAnchor(null);
          }}
        >
          {t(event.enabled ? 'disable' : 'enable')}
        </MenuItem>
        <MenuItem
          className={`${classes.menuItem} ${classes.delete}`}
          disabled={!writeAccess}
          onClick={() => {
            if (!writeAccess) return;
            setAnchor(null);
            onDelete(index);
          }}
        >
          <DeleteOutlineIcon fontSize="small" />
          {t('delete')}
        </MenuItem>
      </Menu>
    </ValueBox>
  );
}
